/**
 * 작성자 : 홍선영
 * 날짜 : 2026.06.18
 * 기능 : 센서 종합현황 위젯(dCd 152) 공용 헬퍼
 */
import { O2_RANGE } from '../../../_constants';
import { SensorGroup, SensorLocation, SensorMetricValue, SensorReading, SensorStatus, SensorSubReading, SensorUnit, SensorWidgetData, SensorWidgetRow } from './types';

// 다중 세부위치 라벨이 비어있을 때(null) 채우는 기본 placeholder. 어댑터·뷰가 공유한다.
export const DEFAULT_SUB_LABEL = '기본';

// 위험 단계 → 값 색상 클래스
export const statusClass = (status?: SensorStatus): string => {
  switch (status) {
    case 'good':
      return 'v-good';
    case 'caution':
      return 'v-caution';
    case 'warning':
      return 'v-warning';
    case 'danger':
      return 'v-danger';
    default:
      return 'v-none';
  }
};

// 측정값이 없을 때 표시값('-')과 none 처리
export const readingDisplay = (reading?: SensorReading): string => (reading && reading.display ? reading.display : '-');

// groups → 평탄화된 unit 목록(테이블 컬럼/카드 셀 순서 보장)
export const flattenUnits = (groups: SensorGroup[]): SensorUnit[] => {
  if (!Array.isArray(groups)) return [];
  return groups.flatMap((g) => (Array.isArray(g.units) ? g.units : []));
};

// 그룹당 최대 unit 개수
export const maxUnitsPerGroup = (groups: SensorGroup[]): number => {
  if (!Array.isArray(groups) || groups.length === 0) return 0;
  return groups.reduce((max, g) => Math.max(max, Array.isArray(g.units) ? g.units.length : 0), 0);
};

/**
 * 센서 종류(컬럼) 가로 페이징.
 * 그룹(2단 헤더)은 페이지 경계에서 쪼개지 않고 통째로 채우며, 페이지당 컬럼(unit) 수가
 * maxCols 를 넘지 않도록 묶는다. 단일 그룹이 maxCols 를 넘는 경우엔 단독 페이지로 둔다.
 * 항상 최소 한 페이지([[]] 포함)를 반환해 호출부 분기를 단순화한다.
 */
export const paginateGroupsByColumns = (groups: SensorGroup[], maxCols: number): SensorGroup[][] => {
  if (!Array.isArray(groups) || groups.length === 0) return [[]];
  const limit = Math.max(1, maxCols);
  const pages: SensorGroup[][] = [];
  let current: SensorGroup[] = [];
  let count = 0;
  groups.forEach((g) => {
    const span = Array.isArray(g.units) ? g.units.length : 0;
    // 현재 페이지에 이미 컬럼이 있고, 이 그룹을 더하면 한도를 넘으면 페이지를 끊는다.
    if (count > 0 && count + span > limit) {
      pages.push(current);
      current = [];
      count = 0;
    }
    current.push(g);
    count += span;
  });
  if (current.length > 0) pages.push(current);
  return pages.length > 0 ? pages : [[]];
};

// ── 센서 통합 위젯 응답(SensorWidgetRow[]) → 렌더 모델(SensorWidgetData) 정규화 ──

// 메트릭 키(응답 필드명). locationName 을 제외한 측정값 필드만.
type SensorMetricKey = Exclude<keyof SensorWidgetRow, 'locationName'>;

// 응답의 고정 센서 메트릭 정의(배열 순서 = 화면 컬럼 순서).
// group 이 같고 연속된 메트릭은 하나의 2단 그룹 헤더로 묶이지만, 현재는 모든 센서가 1단 헤더(각자 다른 group 키).
// srCd : 센서 측정범위 코드. 있으면 센서명(/sensor/ulist cdName)·단위(/sensor/range unit)를 동적으로 사용,
//        없으면(소음/풍속/MQ/진동/경사) label·unitText fallback 을 그대로 쓴다.
interface SensorMetricDef {
  key: SensorMetricKey; // 응답 필드명 = unit key
  srCd?: string; // 센서 측정범위 코드 (ulist ssCd / range srCd 와 매칭)
  group: string; // 그룹 키
  groupLabel: string; // 그룹 헤더 라벨(fallback)
  label: string; // 컬럼(unit) 라벨(fallback)
  unitText?: string; // 측정 단위(fallback)
}

// srCd 는 SENSOR_RANGE 공통코드에 실재가 확인된 것만 둔다(PM10·PM2.5·NO2·O2·VIBRO·CRACK).
// srCd 가 있으면 센서명(/sensor/ulist)·단위(/sensor/range)·위험단계 색상을 동적으로 사용한다.
// srCd 가 없는 센서(소음/풍속/MQ/경사)는 레벨 기준이 없어 색상 없이(none) 아래 라벨·단위로만 표시한다.
const SENSOR_METRICS: SensorMetricDef[] = [
  { key: 'pm10', srCd: 'PM10', group: 'pm10', groupLabel: '미세먼지', label: '미세먼지', unitText: 'µg/m³' },
  { key: 'pm25', srCd: 'PM2.5', group: 'pm25', groupLabel: '초미세먼지', label: '초미세먼지', unitText: 'µg/m³' },
  { key: 'sound', group: 'sound', groupLabel: '소음', label: '소음', unitText: 'dB' },
  { key: 'no2', srCd: 'NO2', group: 'no2', groupLabel: '이산화질소', label: '이산화질소', unitText: 'ppm' },
  { key: 'wind', group: 'wind', groupLabel: '풍속', label: '풍속', unitText: 'm/s' },
  { key: 'o2', srCd: 'O2', group: 'o2', groupLabel: '산소', label: '산소', unitText: '% vol' },
  { key: 'mq', group: 'mq', groupLabel: '가스', label: '가스', unitText: 'ppm' },
  { key: 'vibro', srCd: 'VIBRO', group: 'vibro', groupLabel: '진동', label: '진동', unitText: 'dBV' },
  { key: 'tilt', group: 'tilt', groupLabel: '경사', label: '경사', unitText: '%' },
  { key: 'crack', srCd: 'CRACK', group: 'crack', groupLabel: '균열', label: '균열', unitText: 'mm' },
];

// 센서명 맵 : /sensor/ulist 응답 → { ssCd(=srCd): cdName }
export type SensorNameMap = Record<string, string>;
export const buildSensorNameMap = (sensorUList: any[]): SensorNameMap => {
  const map: SensorNameMap = {};
  if (!Array.isArray(sensorUList)) return map;
  sensorUList.forEach((s) => {
    if (s && s.ssCd && s.cdName) map[s.ssCd] = s.cdName;
  });
  return map;
};

// 위치 무관 positional 컬럼 키 (방식 B 다중 세부위치). 예: posKey('vibro', 0) → 'vibro-0'
export const posKey = (metricKey: string, i: number): string => `${metricKey}-${i}`;

// 메트릭 원본값(단일 number | null | 배열)을 세부위치 배열로 정규화한다.
//  - 배열 : 등록된 세부위치는 미측정(value=null)이어도 라벨을 보존해 유지(옵션 B). NaN 도 null 로.
//           라벨 없으면 '기본'(결정 #1-3). → 측정점 컬럼 구조 유지, 값 없으면 '-' 표시
//  - 단일 number : 라벨 없는 1건(방식 B 인라인 태그 미적용)
export const normalizeSub = (raw: SensorMetricValue | undefined): SensorSubReading[] => {
  if (Array.isArray(raw)) {
    return raw
      .filter((s) => s && typeof s === 'object')
      .map((s) => {
        const n = s.value == null ? null : Number(s.value);
        return { label: (s.label ?? '').trim() || DEFAULT_SUB_LABEL, value: n != null && !Number.isNaN(n) ? n : null };
      });
  }
  if (typeof raw === 'number' && !Number.isNaN(raw)) return [{ value: raw }];
  return [];
};

// 메트릭별 최대 세부위치 수. >=2 면 방식 B inline 그룹(positional 컬럼)으로 렌더한다(결정 #2).
const buildMaxSubMap = (rows: SensorWidgetRow[], metrics: SensorMetricDef[] = SENSOR_METRICS): Record<string, number> => {
  const map: Record<string, number> = {};
  metrics.forEach((m) => {
    map[m.key] = rows.reduce((max, r) => Math.max(max, normalizeSub(r[m.key]).length), 0);
  });
  return map;
};

// 메트릭 정의 + 데이터(maxSubMap) → 그룹 구조.
//  - maxSub >= 2 : 독립 inline 그룹(단일 헤더 colSpan + positional units, 방식 B)
//  - maxSub <= 1 : 기존 동작(연속 동일 group 을 2단 헤더로 병합, 단일 컬럼)
// 라벨은 센서명 맵(nameMap), 단위는 범위 맵(rangeMap.unit) 우선, 없으면 fallback.
const buildMetricGroups = (maxSubMap: Record<string, number>, nameMap: SensorNameMap, rangeMap: SensorRangeMap, metrics: SensorMetricDef[] = SENSOR_METRICS): SensorGroup[] => {
  const labelOf = (m: SensorMetricDef) => (m.srCd && nameMap[m.srCd]) || m.label;
  const unitOf = (m: SensorMetricDef) => (m.srCd && rangeMap[m.srCd]?.unit) || m.unitText;

  const groups: SensorGroup[] = [];
  metrics.forEach((m) => {
    const maxSub = maxSubMap[m.key] ?? 0;
    // 다중 세부위치 → 독립 inline 그룹 (방식 B)
    if (maxSub >= 2) {
      groups.push({
        key: m.key,
        label: labelOf(m),
        inline: true,
        units: Array.from({ length: maxSub }, (_, i) => ({
          key: posKey(m.key, i),
          label: '', // 위치는 셀 인라인 태그가 표기 → 컬럼 라벨 불필요
          unitText: i === 0 ? unitOf(m) : undefined,
        })),
      });
      return;
    }
    // 단일값 → 기존 병합 로직
    const unit: SensorUnit = { key: m.key, label: labelOf(m), unitText: unitOf(m) };
    const last = groups[groups.length - 1];
    if (last && !last.inline && last.key === m.group) last.units.push(unit);
    else groups.push({ key: m.group, label: m.groupLabel, units: [unit] });
  });
  // 단일 컬럼(비 inline) 그룹은 헤더에 group.label 이 표시되므로 동적 센서명으로 교체
  groups.forEach((g) => {
    if (!g.inline && g.units.length === 1) g.label = g.units[0].label;
  });
  return groups;
};

export interface SensorRangeLevels {
  level1: number; // 주의 기준(이상)
  level2: number; // 경고 기준(이상)
  level3: number; // 위험 기준(이상)
  unit?: string;
}
export type SensorRangeMap = Record<string, SensorRangeLevels>;

// /sensor/range 응답 rangeList → srCd 기준 맵 (WGasInfo 동일 방식)
export const buildSensorRangeMap = (rangeList: any[]): SensorRangeMap => {
  const map: SensorRangeMap = {};
  if (!Array.isArray(rangeList)) return map;
  rangeList.forEach((r) => {
    if (!r || !r.srCd || r.delYn === 'Y') return;
    map[r.srCd] = {
      level1: r.level1 ? Number(r.level1) : 0,
      level2: r.level2 ? Number(r.level2) : 0,
      level3: r.level3 ? Number(r.level3) : 0,
      unit: r.unit,
    };
  });
  return map;
};

// 레벨(0~3) → 위험단계
const LEVEL_STATUS: SensorStatus[] = ['good', 'caution', 'warning', 'danger'];

// 산소(O2)는 하한/상한 양방향 기준이라 별도 상수(O2_RANGE)로 평가한다 (WGasInfo 동일 로직)
const o2Status = (value: number): SensorStatus => {
  const matched = [...O2_RANGE].reverse().find((range) => {
    const [lowerMin, lowerMax] = range.lower;
    const [upperMin, upperMax] = range.upper;
    const lowerBreached = (lowerMin !== -1 && value < lowerMin) || (lowerMax !== -1 && value <= lowerMax);
    const upperBreached = (upperMin !== -1 && value > upperMin) || (upperMax !== -1 && value >= upperMax);
    return lowerBreached || upperBreached;
  });
  return LEVEL_STATUS[matched ? matched.level : 0];
};

// 메트릭 값 → 위험단계. 범위 기준(rangeMap)을 사용하며, 기준이 없으면 none.
const statusFromRange = (srCd: string | undefined, value: number, rangeMap: SensorRangeMap): SensorStatus => {
  if (!srCd) return 'none'; // 범위 코드 미정의 센서 → 색 없음
  if (srCd === 'O2') return o2Status(value);
  const range = rangeMap[srCd];
  if (!range || !range.level1 || !range.level2 || !range.level3) return 'none'; // 기준 미설정 → 색 없음
  if (value >= range.level3) return 'danger';
  if (value >= range.level2) return 'warning';
  if (value >= range.level1) return 'caution';
  return 'good';
};

const formatValue = (value: number): string => (Number.isInteger(value) ? String(value) : value.toFixed(1));

/**
 * 센서 통합 위젯 응답(data 배열) → 위젯 렌더 모델(SensorWidgetData) 정규화 어댑터.
 * 위젯/렌더링/페이징은 그대로 두고, 데이터만 이 함수로 변환해 주입한다.
 *  - 행(설치 위치) = locationName
 *  - 열 = 고정 센서 메트릭(SENSOR_METRICS). PM10·PM2.5 는 '미세먼지' 2단 헤더로 묶임
 *  - 다중 세부위치 메트릭(maxSub>=2) = 방식 B inline 그룹(positional 컬럼 + 인라인 위치 태그)
 *  - 센서명(헤더) = /sensor/ulist cdName(nameMap), 단위 = /sensor/range unit(rangeMap). 없으면 fallback
 *  - null/undefined/NaN 값은 readings 에 넣지 않아 '-'(none)로 표시
 *  - 위험단계 색상은 /sensor/range 레벨 기준(rangeMap). 기준 없는 센서는 none(색 없음)
 */
export const sensorRowsToWidgetData = (rows: SensorWidgetRow[], rangeMap: SensorRangeMap = {}, nameMap: SensorNameMap = {}): SensorWidgetData => {
  if (!Array.isArray(rows) || rows.length === 0) return { groups: [], locations: [] };
  // 미사용 종류는 백엔드가 키를 생략(undefined)하므로 컬럼에서 제외한다.
  // 사용중이지만 데이터가 없는 종류는 키가 null 로 존재 → 컬럼 유지(빈 값 '-' 표시).
  const activeMetrics = SENSOR_METRICS.filter((m) => rows.some((r) => r[m.key] !== undefined));
  const maxSubMap = buildMaxSubMap(rows, activeMetrics);
  const locations: SensorLocation[] = rows.map((row, i) => {
    const readings: SensorLocation['readings'] = {};
    activeMetrics.forEach((m) => {
      const subs = normalizeSub(row[m.key]);
      if ((maxSubMap[m.key] ?? 0) >= 2) {
        // 다중 세부위치: 좌측부터 positional 키에 채우고 각 reading 에 label(인라인 태그)
        // 값이 없어도(null) 세부위치 라벨은 표시하도록 빈 reading('-')을 생성한다.
        subs.forEach((s, idx) => {
          readings[posKey(m.key, idx)] =
            s.value == null ? { display: '-', status: 'none', label: s.label } : { display: formatValue(s.value), status: statusFromRange(m.srCd, s.value, rangeMap), label: s.label };
        });
      } else {
        // 단일값: 기존대로 메트릭 키에 1건(태그 없음)
        const first = subs[0];
        if (first && first.value != null) {
          readings[m.key] = { display: formatValue(first.value), status: statusFromRange(m.srCd, first.value, rangeMap) };
        }
      }
    });
    return { key: `${i}-${row.locationName ?? ''}`, name: row.locationName || `위치 ${i + 1}`, readings };
  });
  return { groups: buildMetricGroups(maxSubMap, nameMap, rangeMap, activeMetrics), locations };
};
